import type { AxiosInstance } from 'axios';

import type { HttpParam, HttpQueryParam, HttpRequestPayload } from '@core/services/http/_types';

class HttpCreator {
  constructor(public prefix: string, private client: AxiosInstance) {}

  get = <Response = any, Query = any>(url: string, payload?: HttpRequestPayload<Query>) => {
    return this.client.get<Response>(
      this.getUrl(url, payload?.param, payload?.query),
      payload?.config,
    );
  };

  post = <Response = any, Data = any, Query = any>(
    url: string,
    data?: Data,
    payload?: HttpRequestPayload<Query>,
  ) => {
    return this.client.post<Response>(
      this.getUrl(url, payload?.param, payload?.query),
      data,
      payload?.config,
    );
  };

  put = <Response = any, Data = any, Query = any>(
    url: string,
    data: Data,
    payload?: HttpRequestPayload<Query>,
  ) => {
    return this.client.put<Response>(
      this.getUrl(url, payload?.param, payload?.query),
      data,
      payload?.config,
    );
  };

  patch = <Response = any, Data = any, Query = any>(
    url: string,
    data: Data,
    payload?: HttpRequestPayload<Query>,
  ) => {
    return this.client.patch<Response>(
      this.getUrl(url, payload?.param, payload?.query),
      data,
      payload?.config,
    );
  };

  delete = <Response = any, Query = any>(url: string, payload?: HttpRequestPayload<Query>) => {
    return this.client.delete<Response>(
      this.getUrl(url, payload?.param, payload?.query),
      payload?.config,
    );
  };

  private getUrl = <Query = any>(
    url: string,
    param?: HttpParam,
    queryHttpParam?: HttpQueryParam<Query>,
  ) => {
    let resultUrl = `/${this.prefix}/${url}`;

    const pattern = /:(\w+)/g;
    const match = pattern.exec(resultUrl);

    if (param && match) {
      resultUrl = resultUrl.replace(pattern, (_m, p) => String(param[p]));
    }

    if (queryHttpParam) {
      resultUrl += this.parseQueryHttpParam(queryHttpParam);
    }

    return resultUrl;
  };

  private parseQueryHttpParam = <Query = any>(query: HttpQueryParam<Query>) => {
    return Object.entries(query).reduce((acc, [key, value], idx) => {
      if (value === undefined) {
        return acc;
      }

      let formattedValue: string;
      if (Array.isArray(value)) {
        formattedValue = value.join(',');
      } else {
        formattedValue = `${value}`;
      }

      if (idx === 0) {
        acc += `?${key}=${formattedValue}`;
      } else {
        acc += `&${key}=${formattedValue}`;
      }

      return acc;
    }, '');
  };
}

export const createHTTP = (prefix: string, client: AxiosInstance) =>
  new HttpCreator(prefix, client);
